/**
 * Service layer — Template Images domain (api/template-images.js)
 *
 * Owns the Supabase-hosted image fields consumed by Documint PDF Insert templates:
 *   entity_type 'pdf_insert' field_key 'vicinity_map'   — PDF Insert record's "Vicinity Map"
 *   entity_type 'project'    field_key 'project_image'  — Project's "Project Image"
 *
 * Airtable is never read for these fields — Supabase is the source of truth going forward.
 */

async function clerkToken() {
  return window.__clerkGetToken ? await window.__clerkGetToken() : null
}

/** Returns the current image { id, url, filename, ... } for an entity+field, or null. */
export async function getTemplateImage(entityType, entityAirtableId, fieldKey) {
  const qs = new URLSearchParams({ entityType, entityAirtableId, fieldKey }).toString()
  const res = await fetch('/api/template-images?' + qs)
  const d = await res.json()
  if (!res.ok) throw new Error(d.error || 'getTemplateImage failed')
  return d.image
}

/** Uploads a File as the new current image for an entity+field. Returns the new image row. */
export async function uploadTemplateImage(entityType, entityAirtableId, fieldKey, file) {
  const token = await clerkToken()

  const upRes = await fetch('/api/template-images', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ action: 'getUploadUrl', entityType, entityAirtableId, fieldKey, filename: file.name }),
  })
  const upData = await upRes.json()
  if (!upRes.ok) throw new Error(upData.error || 'getUploadUrl failed')

  const putRes = await fetch(upData.signedUrl, { method: 'PUT', headers: { 'Content-Type': file.type || 'application/octet-stream' }, body: file })
  if (!putRes.ok) throw new Error('Upload to storage failed')

  const confirmRes = await fetch('/api/template-images', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({
      action: 'confirmUpload', entityType, entityAirtableId, fieldKey,
      storagePath: upData.storagePath, publicUrl: upData.publicUrl,
      filename: file.name, contentType: file.type || null, sizeBytes: file.size || null,
    }),
  })
  const confirmData = await confirmRes.json()
  if (!confirmRes.ok) throw new Error(confirmData.error || 'confirmUpload failed')
  return confirmData.image
}

/** Deletes a template image row + its Storage object. */
export async function deleteTemplateImage(imageId) {
  const token = await clerkToken()
  const res = await fetch('/api/template-images', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
    body: JSON.stringify({ action: 'delete', imageId }),
  })
  const d = await res.json()
  if (!res.ok) throw new Error(d.error || 'deleteTemplateImage failed')
  return d.ok
}
